import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import InsightsIcon from '@mui/icons-material/Insights';
import { useSessionStore } from '@/stores/useSessionStore';
import { useInsightStore } from '@/stores/useInsightStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { changePassword } from '@/api/auth';
import { hasPermission } from '@/utils/permissions';
import { useGuardedNavigate } from '@/hooks/useDraftGuard';

interface TopBarProps {
  height: number;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
}

/**
 * 顶部导航栏（56px 高）。
 * 包含 Logo、当前会话标题、设置和洞察按钮。
 */
const TopBar: React.FC<TopBarProps> = ({
  height,
  sidebarOpen,
  onToggleSidebar,
}) => {
  const directNavigate = useNavigate();
  const navigate = useGuardedNavigate();
  const { pathname } = useLocation();
  const pageTitle: Record<string, string> = { '/chat': '练习空间', '/sessions': '会话中心', '/ai-core': 'AI Core', '/knowledge': '知识中心', '/access': '用户与授权', '/config': '配置中心' };
  const currentSession = useSessionStore((s) => s.currentSession);
  const { isOpen: insightOpen, togglePanel } = useInsightStore();
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const logout = useAuthStore((s) => s.logout);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [passwordOpen, setPasswordOpen] = React.useState(false);
  const [oldPassword, setOldPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [passwordBusy, setPasswordBusy] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState('');

  const savePassword = async () => {
    setPasswordBusy(true);
    setPasswordError('');
    try {
      await changePassword(oldPassword, newPassword);
      setPasswordOpen(false);
      setOldPassword('');
      setNewPassword('');
      logout();
      directNavigate('/login');
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : '修改密码失败');
    } finally { setPasswordBusy(false); }
  };

  return (
    <AppBar
      position="relative"
      elevation={0}
      sx={{
        height,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          height,
          minHeight: height,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* 左侧：汉堡菜单 + Logo + 标题 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {!sidebarOpen && (
            <Tooltip title="展开侧边栏">
              <IconButton
                aria-label="展开导航"
                edge="start"
                color="inherit"
                onClick={onToggleSidebar}
                size="small"
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              userSelect: 'none',
              fontSize: 16,
              whiteSpace: 'nowrap',
            }}
          >
            {pageTitle[pathname] || '深流'}
          </Typography>
          {pathname === '/chat' && currentSession && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                ml: 1.5,
                maxWidth: 300,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: { xs: 'none', md: 'block' },
              }}
            >
              {currentSession.title}
            </Typography>
          )}
        </Box>

        {/* 右侧：设置 + 洞察 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {pathname === '/chat' && <Tooltip title={insightOpen ? '关闭洞察' : '打开洞察'}>
            <IconButton
              aria-label={insightOpen ? '关闭洞察' : '打开洞察'}
              color={insightOpen ? 'primary' : 'inherit'}
              onClick={togglePanel}
              size="small"
            >
              <InsightsIcon />
            </IconButton>
          </Tooltip>}
          <Button aria-label="账号菜单" size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: 'text.secondary', maxWidth: { xs: 120, md: 220 }, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {user?.display_name || user?.username || '用户'}
          </Button>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            {hasPermission(permissions, 'config') && <MenuItem onClick={() => { navigate('/config'); setAnchorEl(null); }}>配置与用户权限</MenuItem>}
            {(hasPermission(permissions, 'users') || hasPermission(permissions, 'roles')) && <MenuItem onClick={() => { navigate('/access'); setAnchorEl(null); }}>用户与授权</MenuItem>}
            <MenuItem onClick={() => { setPasswordError(''); setPasswordOpen(true); setAnchorEl(null); }}>修改密码</MenuItem>
            <MenuItem onClick={() => { logout(); directNavigate('/login'); }}>退出登录</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <Dialog open={passwordOpen} onClose={() => !passwordBusy && setPasswordOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>修改密码</DialogTitle>
        <DialogContent>
          {passwordError && <Alert severity="error" sx={{ mt: 1 }}>{passwordError}</Alert>}
          <TextField fullWidth size="small" label="原密码" type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth size="small" label="新密码" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button disabled={passwordBusy} onClick={() => setPasswordOpen(false)}>取消</Button>
            <Button variant="contained" onClick={savePassword} disabled={passwordBusy || !oldPassword || newPassword.length < 8}>{passwordBusy ? '保存中' : '保存'}</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </AppBar>
  );
};

export default TopBar;
